import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import "./user-picker.css";

interface SearchHit {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface SearchResponse {
  items: SearchHit[];
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

function fullName(u: SearchHit): string | null {
  const parts = [u.firstName, u.lastName].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? parts.join(" ") : null;
}

interface Props {
  value: string;
  onChange: (email: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  inputId?: string;
}

/**
 * Email-or-name typeahead for admin pages. The committed value is always an
 * email string (because the rest of the codebase keys users by email). When
 * the admin types a name and picks a suggestion, the email of that user
 * gets filled in. Typing a fresh email that isn't in the system is fine —
 * the field stays free-text and the server will create the user on submit.
 */
export function UserPicker({
  value,
  onChange,
  placeholder,
  disabled,
  required,
  ariaInvalid,
  ariaDescribedBy,
  inputId,
}: Props) {
  const reactId = useId();
  const id = inputId ?? `user-picker-${reactId}`;
  const listId = `${id}-list`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);

  const debounced = useDebounced(value, 250);
  const query = useQuery<SearchResponse>({
    queryKey: ["admin", "users-search", debounced],
    queryFn: () => api<SearchResponse>(`/api/admin/users/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.trim().length >= 2,
    staleTime: 30_000,
    retry: false,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  // Don't show the dropdown if the user has typed an exact email match for
  // their first suggestion (likely they already picked it).
  const showList = open && items.length > 0 && value.trim().length >= 2;

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function pick(u: SearchHit) {
    onChange(u.email);
    setOpen(false);
    setHighlight(-1);
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(items.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter" && highlight >= 0 && items[highlight]) {
      e.preventDefault();
      pick(items[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="user-picker" ref={containerRef}>
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder ?? "Email ou nome"}
        disabled={disabled}
        required={required}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        role="combobox"
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="user-picker__list"
        >
          {items.map((u, i) => {
            const name = fullName(u);
            return (
              <li
                key={u.id}
                role="option"
                aria-selected={i === highlight}
                className={`user-picker__item ${i === highlight ? "user-picker__item--active" : ""}`}
                onMouseDown={(e) => {
                  // mousedown fires before blur, so the click takes effect
                  // before the dropdown closes.
                  e.preventDefault();
                  pick(u);
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span className="user-picker__primary">{name ?? u.email}</span>
                {name && <span className="user-picker__secondary">{u.email}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
